import { useCallback, useMemo } from 'react';

import { getAssetRelatedFilePaths } from '@/entities/assets/helpers/getAssetRelatedFilepaths';
import { useDelegatingRequests } from '@/entities/delegating-request';
import { DelegationDto } from '@/entities/delegating-request/types';
import { getDocumentFilepaths } from '@/entities/document/helpers/getDocumentsFilepaths';
import { Permission } from '@/shared/lib/secure-json/core/secure-json-collection/types';
import { CollectionName, useSJDatabase } from '@/shared/lib/sj-orm';
import { getCollectionNameByDtoId } from '@/shared/lib/sj-orm/helpers';
import { DtoId } from '@/shared/lib/sj-orm/models/base.dto';
import { DelegatingRequestStatus } from '@/shared/lib/sj-orm/models/delegating-request.dto';
import { useAssistantStore } from '@/shared/store/assistant.store';
import { useKeyPairStore } from '@/shared/store/decrypted-keypair.store';
import { useProfileStore } from '@/shared/store/profile.store';

const removeReadWritePermissions = (collection: {
  deletePermission: (id: string, publicKey: string, permission: Permission) => void;
}) => {
  return (id: string, publicKey: string) => {
    collection.deletePermission(id, publicKey, Permission.READ);
    collection.deletePermission(id, publicKey, Permission.WRITE);
  };
};

const collectDtoIds = (value: unknown, ids: Set<DtoId>) => {
  if (typeof value === 'string') {
    if (getCollectionNameByDtoId(value)) {
      ids.add(value);
    }
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => collectDtoIds(item, ids));
    return;
  }

  if (value && typeof value === 'object') {
    Object.values(value as Record<string, unknown>).forEach((item) => collectDtoIds(item, ids));
  }
};

export const useAssistant = () => {
  const { requestId } = useAssistantStore();
  const { getDelegatingRequest, updateDelegatingRequestValue } = useDelegatingRequests();
  const sjDb = useSJDatabase();
  const { keyPair } = useKeyPairStore();
  const { profileId } = useProfileStore();

  const delegation = useMemo(
    () => getDelegatingRequest(requestId),
    [getDelegatingRequest, requestId],
  );

  const updateDelegation = useCallback(
    async (data: Partial<DelegationDto>) => {
      if (!delegation || !profileId || !keyPair?.publicKey) return;
      const assistantPublicKey = keyPair.publicKey;
      const request = delegation.items[0];
      if (!request) return;

      const updatedDelegation = await updateDelegatingRequestValue({
        requestId: delegation.id,
        patch: data,
        status: DelegatingRequestStatus.WAITING_FOR_APPROVE,
      });

      if (!updatedDelegation) {
        return;
      }

      const removeAccessById = (collection?: {
        deletePublicKey: (id: string, publicKey: string) => void;
        deletePermission: (id: string, publicKey: string, permission: Permission) => void;
      }) => {
        if (!collection) {
          return () => {};
        }

        const removePermissions = removeReadWritePermissions(collection);

        return (id: string) => {
          collection.deletePublicKey(id, assistantPublicKey);
          removePermissions(id, assistantPublicKey);
        };
      };

      const removeAssetAccess = removeAccessById(sjDb.collections.assets);
      const removePrivateDocumentAccess = removeAccessById(sjDb.collections.privateDocuments);
      const removePersonalIdAccess = removeAccessById(
        sjDb.collections.personalIdentifiersDocuments,
      );
      const removeFileMetaInfoAccess = removeAccessById(sjDb.collections.fileMetaInfos);
      const removeEncryptedFileKeyAccess = removeAccessById(sjDb.collections.encryptedFileKey);

      const mergedDelegationValue = {
        ...(request.value as Record<string, unknown>),
        ...(data as Record<string, unknown>),
      };
      const dtoIds = new Set<DtoId>();
      const filePaths = new Set<string>();

      collectDtoIds(mergedDelegationValue, dtoIds);
      if (delegation.relatedEntity?.dtoId) {
        dtoIds.add(delegation.relatedEntity.dtoId);
      }

      dtoIds.forEach((dtoId) => {
        const collectionName = getCollectionNameByDtoId(dtoId);
        if (collectionName === CollectionName.ASSETS) {
          const item = sjDb.collections.assets?.findById(dtoId);
          if (item?.isCreatedByAssistant) {
            removeAssetAccess(dtoId);
            getAssetRelatedFilePaths(item).forEach((filePath) => filePaths.add(filePath));
          }
        }

        if (collectionName === CollectionName.PRIVATE_DOCUMENTS) {
          const item = sjDb.collections.privateDocuments?.findById(dtoId);
          if (item?.isCreatedByAssistant) {
            removePrivateDocumentAccess(dtoId);
            getDocumentFilepaths(item).forEach((filePath) => filePaths.add(filePath));
          }
        }

        if (collectionName === CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS) {
          const item = sjDb.collections.personalIdentifiersDocuments?.findById(dtoId);
          if (item?.isCreatedByAssistant) {
            removePersonalIdAccess(dtoId);
            getDocumentFilepaths(item).forEach((filePath) => filePaths.add(filePath));
          }
        }
      });

      if (filePaths.size > 0) {
        const fileMetaInfos =
          sjDb.collections.fileMetaInfos?.findMany((item) => filePaths.has(item.filePath)) ?? [];

        fileMetaInfos.forEach((item) => {
          removeFileMetaInfoAccess(item.id);
        });

        const encryptedFileKeys =
          sjDb.collections.encryptedFileKey?.findMany((item) => {
            const separatorIndex = item.id.indexOf(':');
            const filePath = separatorIndex === -1 ? item.id : item.id.slice(0, separatorIndex);
            return filePaths.has(filePath);
          }) ?? [];

        encryptedFileKeys.forEach((item) => {
          removeEncryptedFileKeyAccess(item.id);
        });
      }

      const delegatingRequestsCollection = sjDb.collections.delegatingRequests;
      if (delegatingRequestsCollection) {
        const removeDelegationPermissions = removeReadWritePermissions(
          delegatingRequestsCollection,
        );
        delegatingRequestsCollection.deletePublicKey(requestId, assistantPublicKey);
        removeDelegationPermissions(requestId, assistantPublicKey);
      }

      // createNotification(
      //   {
      //     type: NotificationType.ASSISTANT_FILLED_FORM,
      //     documentId: delegation.relatedEntity ? delegation.relatedEntity.dtoId : '',
      //     assistantProfileId: profileId ? String(profileId) : '',
      //     delegatingRequest: delegation.id,
      //   },
      //   true,
      //   sjDb.collections.delegatingRequests?.getPublicKeys(requestId),
      // );
    },
    [requestId, profileId, delegation, sjDb, keyPair, updateDelegatingRequestValue],
  );

  return useMemo(
    () => ({
      requestId,
      delegation,
      updateDelegation,
    }),
    [delegation, requestId, updateDelegation],
  );
};
